import React, { useState } from "react";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { PageHero, Reveal, Overline, CTAButton, ArrowLink } from "@/components/primitives";
import { submitForm } from "@/lib/api";
import { trackLead } from "@/lib/pixel";
import { toast } from "sonner";
import { EVENT } from "@/data/site";

const INTERESTS = ["Attend", "Sponsor", "Exhibit", "Speak", "Press", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", interest: "Attend", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-emerald2";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitForm({ form_type: "contact", ...form });
      const [firstName, ...lastParts] = String(form.name).trim().split(/\s+/);
      trackLead("contact", res?.id, { email: form.email, first_name: firstName, last_name: lastParts.join(" ") });
      toast.success("Message sent. We'll route you to the right person.");
      setForm({ name: "", email: "", organisation: "", interest: "Attend", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero overline="Contact" title="Talk to the team." subtitle="Sponsorship, exhibiting, speaking, press or delegate queries — we'll route you to the right person." />

      <section className="bg-parchment py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="rounded-3xl border border-border bg-white p-7 shadow-sm md:p-9">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="contact-name" className={inputCls} placeholder="Full name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="contact-email" type="email" className={inputCls} placeholder="Work email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <input data-testid="contact-org" className={`${inputCls} sm:col-span-2`} placeholder="Organisation" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} />
                  <select data-testid="contact-interest" className={`${inputCls} sm:col-span-2`} value={form.interest} onChange={(e) => set("interest", e.target.value)}>
                    {INTERESTS.map((i) => <option key={i} value={i}>I'm interested in: {i}</option>)}
                  </select>
                  <textarea data-testid="contact-message" rows={5} className={`${inputCls} sm:col-span-2`} placeholder="Your message" value={form.message} onChange={(e) => set("message", e.target.value)} />
                </div>
                <CTAButton type="submit" data-testid="contact-submit" variant="primary" className="mt-5">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send message
                </CTAButton>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-border bg-navy grain p-8 text-slate-300">
                <Overline light className="mb-5">Reach us</Overline>
                <ul className="space-y-4 text-sm">
                  <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-emerald2" /> <a href={`mailto:${EVENT.email}`} className="hover:text-white">{EVENT.email}</a></li>
                  <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-emerald2" /> <span>{EVENT.phone} / {EVENT.phone2}</span></li>
                  <li className="flex items-start gap-3"><MapPin className="h-5 w-5 shrink-0 text-emerald2" /> <span>{EVENT.address}</span></li>
                </ul>
                <div className="mt-8 space-y-3 border-t border-white/10 pt-6">
                  <ArrowLink to="/sponsor" light>Sponsorship deck</ArrowLink><br />
                  <ArrowLink to="/exhibit" light>Exhibitor floor plan</ArrowLink><br />
                  <ArrowLink to="/conference/agenda" light>Download brochure</ArrowLink>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
